
import React from 'react';
import { Career } from '../types';

interface CareerSectionProps {
  careers: Career[];
}

const CareerSection: React.FC<CareerSectionProps> = ({ careers }) => {
  return (
    <section className="py-20 px-4 bg-gray-50 dark:bg-gray-900/50 transition-colors">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-blue-800 dark:text-blue-400 font-black text-sm uppercase tracking-widest mb-2 transition-colors">Careers</h2>
          <h3 className="text-4xl font-extrabold text-gray-900 dark:text-white mb-4 transition-colors">Build Your Future with Sai SSK</h3>
          <p className="text-gray-600 dark:text-gray-400 max-w-xl mx-auto transition-colors">
            We are always looking for passionate engineers, operators, and specialists to join our award-winning infrastructure teams.
          </p>
        </div>

        {careers.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm italic text-gray-400 dark:text-gray-500 transition-colors">
            No current openings. Please check back later or send a general application.
          </div>
        ) : (
          <div className="space-y-4">
            {careers.map(job => (
              <div key={job.id} className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-blue-100 dark:border-gray-700 shadow-sm hover:shadow-md transition group">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div>
                    <h4 className="text-xl font-bold text-blue-900 dark:text-blue-300 group-hover:text-blue-700 dark:group-hover:text-blue-200 transition-colors">{job.title}</h4>
                    <div className="flex flex-wrap gap-4 mt-1 text-sm text-gray-500 dark:text-gray-400 font-medium transition-colors">
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                        {job.department}
                      </span>
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                        {job.location}
                      </span>
                      <span className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded text-xs uppercase font-bold transition-colors">
                        {job.type}
                      </span>
                    </div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400 text-sm whitespace-pre-wrap transition-colors">{job.description}</p>
                  </div>
                  <button className="whitespace-nowrap bg-blue-800 dark:bg-blue-600 text-white px-6 py-2 rounded-lg font-bold text-sm hover:bg-blue-700 dark:hover:bg-blue-500 transition shadow-sm">
                    Apply Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <div className="mt-12 bg-blue-900 dark:bg-blue-800 rounded-3xl p-12 text-white text-center relative overflow-hidden transition-colors">
          <div className="relative z-10">
            <h4 className="text-2xl font-bold mb-4">Don't see a perfect fit?</h4>
            <p className="text-blue-200 dark:text-blue-300 mb-8 max-w-md mx-auto transition-colors">Send us your CV anyway. We're constantly expanding and love meeting talented professionals.</p>
            <button className="bg-white dark:bg-gray-100 text-blue-900 dark:text-blue-900 px-8 py-3 rounded-full font-bold hover:bg-blue-50 transition">
              General Application
            </button>
          </div>
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full"></div>
          <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-blue-400/20 rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

export default CareerSection;
